import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { POST_LOGIN_REDIRECT_KEY } from '../components/ProtectedRoute';
import TurnstileWidget from '../components/TurnstileWidget';
import './Auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired')) {
      sessionStorage.removeItem('sessionExpired');
      setSessionExpired(true);
    }
  }, []);
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileKey((k) => k + 1);
  };

  const resolvePostLoginPath = () => {
    const from = location.state?.from;
    if (from?.pathname) {
      return `${from.pathname}${from.search || ''}`;
    }
    const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (stored && stored.startsWith('/') && !stored.startsWith('//')) {
      return stored;
    }
    return '/picks';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setShowResendVerification(false);
    setSessionExpired(false);

    if (!turnstileToken) {
      setError('Please complete the captcha');
      return;
    }

    setLoading(true);

    const result = await login(email, password, turnstileToken);
    setLoading(false);

    if (result.success) {
      const target = resolvePostLoginPath();
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      navigate(target);
    } else {
      setError(result.error);
      setShowResendVerification(result.code === 'EMAIL_NOT_VERIFIED');
      resetTurnstile();
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      setInfo('');
      setError('Enter your email above, then click resend.');
      return;
    }
    setError('');
    setInfo('');
    setResendLoading(true);
    const result = await resendVerification(email.trim());
    setResendLoading(false);
    if (result.success) {
      setInfo(result.message || 'If the account exists and is not verified, we sent a new link.');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-title">Welcome Back</h1>
          <p className="auth-subtitle">Log in to place your picks</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-field">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              data-testid="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div className="form-field">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          <TurnstileWidget key={turnstileKey} onTokenChange={setTurnstileToken} />

          {sessionExpired && <div className="auth-info">Your session has expired. Please log in again.</div>}
          {error && <div className="auth-error">{error}</div>}
          {info && <div className="auth-info">{info}</div>}

          {showResendVerification && (
            <button
              type="button"
              className="auth-link"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: '0.75rem', font: 'inherit', textDecoration: 'underline', alignSelf: 'flex-start' }}
              onClick={handleResend}
              disabled={resendLoading}
            >
              {resendLoading ? 'Sending…' : 'Resend verification email'}
            </button>
          )}

          <button
            type="submit"
            className="auth-btn"
            data-testid="login-submit"
            disabled={loading || !turnstileToken}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Don't have an account? <Link to="/register" className="auth-link">Register here</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Login;
